import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useProductStock } from '@/contexts/InventoryContext';
import { PRODUCTS } from '@/constants/testIds';

export default function ProductCard({ product, index = 0 }) {
  const { lang, t } = useLang();
  const { add } = useCart();
  const { stock, isLow, isOut, known } = useProductStock(product.slug);
  const onQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOut) {
      toast.error(lang === 'es' ? 'Producto agotado 😿' : 'Sold out 😿');
      return;
    }
    add(product.slug, -1, 1);
    toast.success(lang === 'es' ? '¡Añadido al carrito!' : 'Added to cart!');
  };
  return (
    <Link
      to={`/productos/${product.slug}`}
      data-testid={PRODUCTS.card(product.slug)}
      className="group relative block rounded-3xl overflow-hidden border border-[#FF7597]/10 bg-white hover:shadow-[0_24px_60px_rgba(255,117,151,0.18)] transition-all duration-500 hover:-translate-y-1"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div
        className="relative aspect-[4/5] overflow-hidden"
        style={{ backgroundColor: product.color }}
      >
        <div className="absolute inset-0 grain" />
        <img
          src={product.image}
          alt={product.name[lang]}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-700"
        />
        <span
          className="absolute top-4 left-4 text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full bg-white/80 backdrop-blur"
          style={{ color: product.accent }}
        >
          Jelly
        </span>
        {known && isOut && (
          <span
            data-testid={`stock-out-${product.slug}`}
            className="absolute bottom-4 left-4 text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 rounded-full bg-[#1F1A24] text-white shadow-lg"
          >
            {lang === 'es' ? '🚫 AGOTADO' : '🚫 SOLD OUT'}
          </span>
        )}
        {known && isLow && !isOut && (
          <span
            data-testid={`stock-low-${product.slug}`}
            className="absolute bottom-4 left-4 text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 rounded-full bg-[#FF2D9C] text-white shadow-lg animate-pulse"
          >
            {lang === 'es' ? `⚡ ÚLTIMAS ${stock}` : `⚡ ONLY ${stock} LEFT`}
          </span>
        )}
        <span className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/90 backdrop-blur inline-flex items-center justify-center group-hover:bg-[#FF7597] group-hover:text-white transition-colors">
          <ArrowUpRight className="w-4 h-4" />
        </span>
        <button
          onClick={onQuickAdd}
          disabled={isOut}
          data-testid={`product-quick-add-${product.slug}`}
          aria-label="Añadir al carrito"
          className={`absolute bottom-4 right-4 h-10 w-10 rounded-full inline-flex items-center justify-center shadow-lg transition-all ${
            isOut
              ? 'bg-[#A99BE0]/50 text-white cursor-not-allowed'
              : 'bg-[#FF2D9C] text-white hover:bg-[#FF1F8A] hover:scale-110'
          }`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="p-5 md:p-6">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: product.accent }}>
          {product.tagline[lang]}
        </p>
        <div className="mt-2 flex items-baseline justify-between gap-3">
          <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-[#1F1A24] leading-tight">
            {product.name[lang]}
          </h3>
          <span className="font-display font-bold text-base text-[#FF7597]">{product.price}</span>
        </div>
        <p className="mt-3 text-xs text-[#574A66] line-clamp-2">{product.description[lang]}</p>
        <span
          data-testid={PRODUCTS.viewBtn(product.slug)}
          className="mt-4 inline-flex items-center text-xs font-bold tracking-widest uppercase text-[#1F1A24] group-hover:text-[#FF7597]"
        >
          {t('products.view')} →
        </span>
      </div>
    </Link>
  );
}
